import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { US_STATES } from '../../core/profile';
import type { ValidationReason } from '../../core/types';
import { ApiError } from '../api';
import { fieldMessage, MONTH_NAMES, stateName } from '../format';

/**
 * One form for every entry (R2, R6, R7) and the name and title (R1). The section says which
 * fields it has, how to check them (the core's own validators), and what body to send. A
 * refusal shows under its field, whether the browser or the Worker found it.
 */
export type Values = Record<string, string | boolean>;
export type Refusal = { field: string; reason: ValidationReason } | null;
export interface FieldSpec {
  name: string;
  label: string;
  kind: 'text' | 'textarea' | 'year' | 'month' | 'state' | 'checkbox';
  max?: number;
  /** Shown only when this holds, e.g. the end date unless "Current role" is ticked. */
  show?: (values: Values) => boolean;
}

const pad = (m: number) => String(m).padStart(2, '0');
const splitMonth = (value: string) => { const [y = '', m = ''] = value.split('-'); return { y, m }; };
const joinMonth = (y: string, m: string) => (y || m ? `${y}-${m}` : '');

export function EntryForm({ title, submitLabel, fields, initial, validate, onSubmit, onCancel }: {
  title: string;
  submitLabel: string;
  fields: FieldSpec[];
  initial: Values;
  validate(values: Values): Refusal;
  /** Resolves once saved; an `ApiError` naming a field shows under that field. */
  onSubmit(values: Values): Promise<void>;
  onCancel(): void;
}) {
  const id = useId();
  const [values, setValues] = useState<Values>(initial);
  const [refusal, setRefusal] = useState<Refusal>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (name: string, value: string | boolean) => { setValues((v) => ({ ...v, [name]: value })); setRefusal((r) => (r?.field === name ? null : r)); };
  const visible = fields.filter((f) => !f.show || f.show(values));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const found = validate(values);
    setRefusal(found); setFailure(null);
    if (found) { document.getElementById(`${id}-${found.field}`)?.focus(); return; }
    setSaving(true);
    try {
      await onSubmit(values);
    } catch (error) {
      if (error instanceof ApiError && typeof error.body.field === 'string') setRefusal({ field: error.body.field, reason: String(error.body.reason ?? 'invalid') as ValidationReason });
      else setFailure(error instanceof ApiError && error.status === 409 ? 'This section is full. Remove an entry to add another.' : 'That didn’t save. Try again.');
      setSaving(false);
    }
  };

  const errorFor = (f: FieldSpec) => (refusal?.field === f.name ? fieldMessage(f.label, refusal.reason, f.max) : null);
  const describe = (f: FieldSpec) => (errorFor(f) ? { 'aria-invalid': true, 'aria-describedby': `${id}-${f.name}-error` } : {});

  return <form className="entry-form" aria-label={title} onSubmit={(e) => void submit(e)} noValidate>
    {visible.map((f) => {
      const error = errorFor(f);
      const control = `${id}-${f.name}`;
      const value = values[f.name];
      let input;
      if (f.kind === 'checkbox') {
        return <label key={f.name} className="check-field"><input id={control} type="checkbox" checked={value === true} onChange={(e) => set(f.name, e.target.checked)} /> {f.label}</label>;
      } else if (f.kind === 'month') {
        const { y, m } = splitMonth(String(value ?? ''));
        return <fieldset key={f.name} className="field month-field">
          <legend>{f.label}</legend>
          <div className="month-inputs">
            <select id={control} aria-label={`${f.label} month`} value={m} onChange={(e) => set(f.name, joinMonth(y, e.target.value))} {...describe(f)}>
              <option value="">Month</option>
              {MONTH_NAMES.map((name, i) => <option key={name} value={pad(i + 1)}>{name}</option>)}
            </select>
            <input aria-label={`${f.label} year`} inputMode="numeric" maxLength={4} placeholder="Year" value={y} onChange={(e) => set(f.name, joinMonth(e.target.value.replace(/\D/g, ''), m))} {...describe(f)} />
          </div>
          {error && <p id={`${control}-error`} className="field-error">{error}</p>}
        </fieldset>;
      } else if (f.kind === 'state') {
        input = <select id={control} value={String(value ?? '')} onChange={(e) => set(f.name, e.target.value)} {...describe(f)}>
          <option value="">Choose a state</option>
          {US_STATES.map((code) => <option key={code} value={code}>{stateName(code)}</option>)}
        </select>;
      } else if (f.kind === 'textarea') {
        input = <textarea id={control} rows={3} value={String(value ?? '')} onChange={(e) => set(f.name, e.target.value)} {...describe(f)} />;
      } else {
        input = <input id={control} type="text" inputMode={f.kind === 'year' ? 'numeric' : undefined} maxLength={f.kind === 'year' ? 4 : undefined} value={String(value ?? '')} onChange={(e) => set(f.name, e.target.value)} {...describe(f)} />;
      }
      return <div key={f.name} className="field">
        <label htmlFor={control}>{f.label}</label>
        {input}
        {error && <p id={`${control}-error`} className="field-error">{error}</p>}
      </div>;
    })}
    {failure && <p className="field-error" role="alert">{failure}</p>}
    <div className="form-actions">
      <button type="submit" className="primary" disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
      <button type="button" className="text-button" onClick={onCancel}>Cancel</button>
    </div>
  </form>;
}

/** What a section's owner can do; each resolves once the Worker has answered. */
export interface EntryOps<Body> {
  add(body: Body): Promise<void>;
  edit(id: string, body: Body): Promise<void>;
  remove(id: string): Promise<void>;
  move(id: string, delta: -1 | 1): Promise<void>;
}

export interface EntryFormSpec<Body> {
  fields: FieldSpec[];
  initial: Values;
  validate(values: Values): Refusal;
  toBody(values: Values): Body;
}

/**
 * A section of entries (R6, R7, R2). The owner gets one prompt while it is empty (R8), then
 * add, edit, remove, and Move up / Move down buttons that work from a keyboard, touch, or a
 * screen reader. A visitor gets the entries only, and no section at all when there are none.
 */
export function EntrySection<E extends { id: string }, Body>({ heading, noun, entries, ops, label, render, form }: {
  heading: string;
  noun: string;
  entries: readonly E[];
  ops?: EntryOps<Body>;
  /** How a button names this entry: "Move <label> up". */
  label(entry: E): string;
  render(entry: E): ReactNode;
  form(entry: E | null): EntryFormSpec<Body>;
}) {
  const id = useId();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  if (!ops && entries.length === 0) return null;
  const article = /^[aeiou]/i.test(noun) ? 'an' : 'a';
  const attempt = async (work: Promise<void>) => {
    setFailure(null);
    try { await work; } catch { setFailure('That didn’t save. Try again.'); }
  };
  const formFor = (entry: E | null) => {
    const spec = form(entry);
    return <EntryForm
      title={entry ? `Edit ${label(entry)}` : `Add ${article} ${noun}`}
      submitLabel={`Save ${noun}`}
      fields={spec.fields}
      initial={spec.initial}
      validate={spec.validate}
      onCancel={() => setEditing(null)}
      onSubmit={async (values) => {
        const body = spec.toBody(values);
        if (entry) await ops!.edit(entry.id, body); else await ops!.add(body);
        setEditing(null);
      }}
    />;
  };

  return <section className="profile-section" aria-labelledby={`${id}-h`}>
    <h2 id={`${id}-h`}>{heading}</h2>
    {entries.length > 0 && <ol className="entries">
      {entries.map((entry, index) => <li key={entry.id} className="entry">
        {editing === entry.id ? formFor(entry) : <>
          {render(entry)}
          {ops && <div className="entry-controls">
            <button type="button" className="text-button" aria-disabled={index === 0} aria-label={`Move ${label(entry)} up`} onClick={() => { if (index > 0) void attempt(ops.move(entry.id, -1)); }}>Move up</button>
            <button type="button" className="text-button" aria-disabled={index === entries.length - 1} aria-label={`Move ${label(entry)} down`} onClick={() => { if (index < entries.length - 1) void attempt(ops.move(entry.id, 1)); }}>Move down</button>
            <button type="button" className="text-button" aria-label={`Edit ${label(entry)}`} onClick={() => setEditing(entry.id)}>Edit</button>
            {confirming === entry.id
              ? <span className="confirm-remove">Remove this {noun}?{' '}
                <button type="button" className="text-button danger" onClick={() => { setConfirming(null); void attempt(ops.remove(entry.id)); }}>Remove</button>{' '}
                <button type="button" className="text-button" onClick={() => setConfirming(null)}>Keep</button>
              </span>
              : <button type="button" className="text-button" aria-label={`Remove ${label(entry)}`} onClick={() => setConfirming(entry.id)}>Remove</button>}
          </div>}
        </>}
      </li>)}
    </ol>}
    {failure && <p className="field-error" role="alert">{failure}</p>}
    {ops && (editing === 'new'
      ? formFor(null)
      : <button type="button" className={entries.length === 0 ? 'empty-prompt' : 'secondary'} onClick={() => setEditing('new')}>
        {entries.length === 0 ? `Add your first ${noun}` : `Add ${article} ${noun}`}
      </button>)}
  </section>;
}
